import type { ReactNode, SyntheticEvent } from 'react';
import React, { useRef } from 'react';
import { OverlayListContainer } from '@/app/common/filters/surfaces/bar/pills/styled.ts';
import { HEIGHT_OF_DESKTOP_HEADER } from '@/app/components/search/components/Filters/HorizontalFilters/Pill/PillOverlay/utils.ts';
import ModalFooter from '@/app/components/search/components/Modal/LayoutElements/ModalFooter';
import PopoverOrModal from '@/app/common/components/PopoverOrModal';
import { MODAL_SIZES } from '@/app/components/search/components/Modal/OpenXModal/types.ts';
import { CUSTOM_GROUP_NAME, FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { useFiltersUiData } from '@/app/common/filters/surfaces/provider/filtersUiProvider.tsx';

const MAX_OVERLAY_HEIGHT = 660;
const overlayIdFor = (pillId: string) => `filter-group-overlay-${pillId.toLowerCase()}`;

export const SORT_OVERLAY_ID = overlayIdFor(FILTER_NAME.SORT);
export const FACILITIES_OVERLAY_ID = overlayIdFor(CUSTOM_GROUP_NAME.FACILITIES);
const BUDGET_OVERLAY_ID = overlayIdFor(FILTER_NAME.PRICE_RANGE);

const DEFAULT_SINGLE_COLUMN_WIDTH = '304px';

const SINGLE_COLUMN_WIDTHS: Record<string, string> = {
  [SORT_OVERLAY_ID]: '312px',
};

const EVO_SINGLE_COLUMN_WIDTHS: Record<string, string> = {
  ...SINGLE_COLUMN_WIDTHS,
  [FACILITIES_OVERLAY_ID]: '449px',
  // The budget histogram is drawn at 288px, which with the overlay's 24px sides and 1px border makes 338.
  [BUDGET_OVERLAY_ID]: '338px',
};

// Keyed by the overlay id FilterGroupPill builds; a key that stops matching falls back silently.
export const getOverlayDimensions = (
  columns: number,
  overlayId: string,
  useEvoFilters: boolean
): { width: string; modalSize: MODAL_SIZES } => {
  if (columns === 2) return { width: '504px', modalSize: MODAL_SIZES.L_EVO };

  const widths = useEvoFilters ? EVO_SINGLE_COLUMN_WIDTHS : SINGLE_COLUMN_WIDTHS;
  return { width: widths[overlayId] ?? DEFAULT_SINGLE_COLUMN_WIDTH, modalSize: MODAL_SIZES.M };
};

type FilterGroupOverlayProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  isModalViewport: boolean;
  pillRef: React.RefObject<HTMLDivElement | null>;
  filterPillFiltersComponents: (() => ReactNode) | ReactNode;
  handleClear?: (e: SyntheticEvent) => void;
  handleApply?: (e: SyntheticEvent) => void;
  isClearDisabled?: boolean;
  applyText?: string;
  isApplyDisabled?: boolean;
  isApplyLoading?: boolean;
  columns: number;
  overlayTitle?: string;
  overlayId?: string;
  closeOnScroll?: boolean;
  forceModal?: boolean;
};

const RenderFunctionOrJSX = ({ component }: { component: (() => ReactNode) | ReactNode }): ReactNode => {
  if (typeof component === 'function') {
    return component();
  }
  return component;
};

const FilterGroupOverlay = ({
  isOpen,
  setIsOpen,
  isLoading,
  isModalViewport,
  pillRef,
  filterPillFiltersComponents,
  handleClear,
  handleApply,
  isClearDisabled,
  applyText,
  isApplyDisabled,
  isApplyLoading,
  columns,
  overlayTitle,
  overlayId = 'filter-group-overlay',
  closeOnScroll = false,
  forceModal = false,
}: FilterGroupOverlayProps) => {
  const overlayListRef = useRef<HTMLDivElement>(null);
  const { useEvoFilters } = useFiltersUiData();
  const { width, modalSize } = getOverlayDimensions(columns, overlayId, !!useEvoFilters);
  const isPopoverWithFooter = !!handleApply && !isModalViewport && !forceModal;

  return (
    <PopoverOrModal
      anchorEl={pillRef.current}
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      isMobile={isModalViewport}
      forceModal={forceModal}
      size={modalSize}
      id={overlayId}
      title={overlayTitle}
      showTitleInPopover={true}
      evo={true}
      {...(overlayTitle ? { 'aria-label': overlayTitle } : {})}
      borderRadius="16px"
      padding="0"
      align="start"
      closeOnScroll={closeOnScroll}
      width={width}
      maxHeight={`min(${MAX_OVERLAY_HEIGHT}px, calc(100vh - ${HEIGHT_OF_DESKTOP_HEADER}px))`}
      bodyBackgroundColor="gray50"
      bodyPadding={0}
      bodyPaddingS={0}
      bodyPaddingXS={0}
      footer={
        handleApply ? (
          <ModalFooter
            evo={true}
            sticky={true}
            layout={isModalViewport ? 'full-width' : 'right'}
            applyText={applyText}
            handleApply={handleApply}
            handleClear={handleClear}
            isApplyDisabled={isApplyDisabled ?? isLoading}
            isApplyLoading={isApplyLoading}
            isClearDisabled={isLoading || isClearDisabled}
          />
        ) : undefined
      }
    >
      <OverlayListContainer
        ref={overlayListRef}
        $padTop={overlayTitle ? 0 : 16}
        $padBottom={isPopoverWithFooter ? 0 : 16}
        $padHorizontal={isModalViewport ? 16 : 24}
      >
        <RenderFunctionOrJSX component={filterPillFiltersComponents} />
      </OverlayListContainer>
    </PopoverOrModal>
  );
};

export default FilterGroupOverlay;
